import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';

// Self-hosted fonts (replaces Google Fonts CDN)
import '@fontsource/montserrat/200.css';
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/cormorant-garamond/300.css';
import '@fontsource/cormorant-garamond/400.css';
import '@fontsource/cormorant-garamond/500.css';
import '@fontsource/cormorant-garamond/600.css';
import '@fontsource/cormorant-garamond/300-italic.css';
import '@fontsource/cormorant-garamond/400-italic.css';

import './styles/variables.css';
import './index.css';
import App from './App';
import { shouldReport } from './utils/sentryGate';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,        // 30s — most data tolerates this
      gcTime: 5 * 60_000,       // 5min — reasonable cache window
    },
  },
});

// SYS-09: only real users on the real site send events (src/utils/sentryGate.js).
// Decided once per page load.
const SENTRY_ENABLED = shouldReport({
  nodeEnv: process.env.NODE_ENV,
  hostname: window.location.hostname,
  userAgent: window.navigator.userAgent,
});

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  enabled: SENTRY_ENABLED,
  tracesSampleRate: 0.1,
  sendDefaultPii: false,
  beforeSend(event) {
    // Second line of defence: nothing leaves when the gate is closed.
    if (!SENTRY_ENABLED) return null;
    delete event.user;
    return event;
  }
});

// ChunkLoadError recovery — stale chunks after deploy cause 404
// Only reload once per session to prevent infinite loop
window.addEventListener('error', (e) => {
  if (e.message?.includes('ChunkLoadError') || e.message?.includes('Loading chunk') || e.message?.includes('Loading CSS chunk')) {
    if (!sessionStorage.getItem('chunk_reload')) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Analytics />
    </QueryClientProvider>
  </React.StrictMode>
);
